import React, { useState } from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';
import clientReviewService from '../../../services/clientReviewService';

const TestimonialModerationPanel = ({ reviews, onStatusUpdate, onRefresh }) => {
  const [selectedReview, setSelectedReview] = useState(null);
  const [showModal, setShowModal] = useState(false);
  const [moderationNotes, setModerationNotes] = useState('');
  const [actionType, setActionType] = useState('');
  const [processing, setProcessing] = useState(false);

  const pendingReviews = reviews?.filter(r => r?.reviewStatus === 'submitted') || [];
  const approvedReviews = reviews?.filter(r => r?.reviewStatus === 'approved') || [];

  const handleAction = (review, action) => {
    setSelectedReview(review);
    setActionType(action);
    setShowModal(true);
  };

  const handleConfirmAction = async () => {
    if (!selectedReview) return;

    try {
      setProcessing(true);

      if (actionType === 'approved') {
        // Auto-generate case study and publish
        await clientReviewService?.approveReviewWithAutoPublish(
          selectedReview?.id,
          moderationNotes,
          'Manager' // In production, use actual user email
        );
      } else if (actionType === 'rejected') {
        await clientReviewService?.rejectReview(
          selectedReview?.id,
          moderationNotes,
          'Manager'
        );
      }

      setShowModal(false);
      setSelectedReview(null);
      setModerationNotes('');
      setActionType('');
      
      // Refresh data
      if (onRefresh) {
        await onRefresh();
      }
    } catch (err) {
      console.error('Action failed:', err);
      alert(err?.message || 'Failed to process review');
    } finally {
      setProcessing(false);
    }
  };

  const renderStars = (rating) => {
    return (
      <div className="flex items-center gap-1">
        {[...Array(5)]?.map((_, index) => (
          <Icon
            key={index}
            name="Star"
            size={14}
            className={index < rating ? 'text-amber-400 fill-amber-400' : 'text-gray-300 dark:text-gray-600'}
          />
        ))}
      </div>
    );
  };

  return (
    <div className="space-y-6">
      {/* Pending Reviews */}
      <div className="bg-white dark:bg-slate-800 rounded-lg shadow-sm border border-gray-200 dark:border-slate-700">
        <div className="p-6 border-b border-gray-200 dark:border-slate-700">
          <div className="flex items-center justify-between">
            <div>
              <h2 className="text-xl font-semibold text-gray-900 dark:text-white">Pending Moderation</h2>
              <p className="text-sm text-gray-600 dark:text-gray-400 mt-1">{pendingReviews?.length} reviews awaiting approval</p>
            </div>
          </div>
        </div>
        <div className="p-6">
          {pendingReviews?.length === 0 ? (
            <div className="text-center py-12">
              <Icon name="CheckCircle" size={48} className="text-gray-300 dark:text-gray-600 mx-auto mb-4" />
              <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-2">All Caught Up!</h3>
              <p className="text-sm text-gray-600 dark:text-gray-400">No reviews pending moderation</p>
            </div>
          ) : (
            <div className="space-y-4">
              {pendingReviews?.map((review) => (
                <div key={review?.id} className="border border-gray-200 dark:border-slate-700 rounded-lg p-4">
                  <div className="flex items-start justify-between mb-3">
                    <div className="flex-1">
                      <div className="flex items-center gap-3 mb-2">
                        <h3 className="font-semibold text-gray-900 dark:text-white">{review?.reviewerName}</h3>
                        {renderStars(review?.starRating)}
                      </div>
                      <p className="text-sm text-gray-600 dark:text-gray-400">{review?.reviewerPosition} • {review?.companyName}</p>
                    </div>
                  </div>
                  <p className="text-sm text-gray-700 dark:text-gray-300 mb-4">{review?.reviewText}</p>
                  <div className="flex items-center gap-4 mb-4 text-xs text-gray-500 dark:text-gray-500">
                    <span className="flex items-center gap-1">
                      <Icon name="DollarSign" size={14} />
                      K{(review?.costSavingsKina || 0)?.toLocaleString()}
                    </span>
                    <span className="flex items-center gap-1">
                      <Icon name="TrendingUp" size={14} />
                      {review?.timeEfficiencyGainPercentage || 0}%
                    </span>
                    <span className="flex items-center gap-1">
                      <Icon name="Shield" size={14} />
                      {review?.regulatoryAdherenceScore || 0}%
                    </span>
                  </div>
                  <div className="flex items-center gap-2">
                    <Button size="sm" onClick={() => handleAction(review, 'approved')} disabled={processing}>
                      <Icon name="Check" size={16} className="mr-1" />
                      Approve & Auto-Publish
                    </Button>
                    <Button size="sm" variant="outline" onClick={() => handleAction(review, 'rejected')} disabled={processing}>
                      <Icon name="X" size={16} className="mr-1" />
                      Reject
                    </Button>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
      {/* Approved Reviews */}
      <div className="bg-white dark:bg-slate-800 rounded-lg shadow-sm border border-gray-200 dark:border-slate-700">
        <div className="p-6 border-b border-gray-200 dark:border-slate-700">
          <div className="flex items-center justify-between">
            <div>
              <h2 className="text-xl font-semibold text-gray-900 dark:text-white">Approved Reviews</h2>
              <p className="text-sm text-gray-600 dark:text-gray-400 mt-1">{approvedReviews?.length} reviews ready for publication</p>
            </div>
          </div>
        </div>
        <div className="p-6">
          {approvedReviews?.length === 0 ? (
            <div className="text-center py-12">
              <Icon name="MessageSquare" size={48} className="text-gray-300 dark:text-gray-600 mx-auto mb-4" />
              <p className="text-sm text-gray-600 dark:text-gray-400">No approved reviews</p>
            </div>
          ) : (
            <div className="space-y-4">
              {approvedReviews?.map((review) => (
                <div key={review?.id} className="border border-gray-200 dark:border-slate-700 rounded-lg p-4">
                  <div className="flex items-start justify-between mb-3">
                    <div className="flex-1">
                      <div className="flex items-center gap-3 mb-2">
                        <h3 className="font-semibold text-gray-900 dark:text-white">{review?.reviewerName}</h3>
                        {renderStars(review?.starRating)}
                      </div>
                      <p className="text-sm text-gray-600 dark:text-gray-400">{review?.companyName}</p>
                    </div>
                  </div>
                  <p className="text-sm text-gray-700 dark:text-gray-300 mb-4">{review?.reviewText}</p>
                  <div className="flex items-center gap-2">
                    <Button size="sm" onClick={() => handleAction(review, 'published')}>
                      <Icon name="Upload" size={16} className="mr-1" />
                      Publish
                    </Button>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
      {/* Moderation Modal */}
      {showModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white dark:bg-slate-800 rounded-lg shadow-xl max-w-md w-full">
            <div className="p-6 border-b border-gray-200 dark:border-slate-700">
              <h3 className="text-lg font-semibold text-gray-900 dark:text-white">
                {actionType === 'approved' ? 'Approve & Auto-Publish Review' : 'Reject Review'}
              </h3>
              {actionType === 'approved' && (
                <p className="text-sm text-gray-600 dark:text-gray-400 mt-2">
                  This will generate an AI case study and automatically publish it to the Public Testimonial Showcase.
                </p>
              )}
            </div>
            <div className="p-6">
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                {actionType === 'rejected' ? 'Rejection Reason' : 'Approval Notes (Optional)'}
              </label>
              <textarea
                value={moderationNotes}
                onChange={(e) => setModerationNotes(e?.target?.value)}
                rows={4}
                className="w-full px-3 py-2 border border-gray-300 dark:border-slate-600 rounded-lg focus:ring-2 focus:ring-blue-500 dark:bg-slate-700 dark:text-white"
                placeholder={actionType === 'rejected' ? 'Reason for rejection...' : 'Optional notes...'}
                disabled={processing}
              />
            </div>
            <div className="p-6 border-t border-gray-200 dark:border-slate-700 flex items-center justify-end gap-2">
              <Button variant="outline" onClick={() => setShowModal(false)} disabled={processing}>
                Cancel
              </Button>
              <Button onClick={handleConfirmAction} disabled={processing}>
                {processing ? (
                  <>
                    <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                    Processing...
                  </>
                ) : (
                  'Confirm'
                )}
              </Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default TestimonialModerationPanel;